import React from 'react';
import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import TimePicker from 'react-multi-date-picker/plugins/time_picker';
import { useTranslation } from 'react-i18next';
import './PersianDatePicker.css';

/**
 * ورودی تاریخ پویا (شمسی/میلادی) — لایه‌ی مشترک روی react-multi-date-picker.
 *
 * در صورت فعال بودن زبان فارسی، تقویم شمسی و در غیر این صورت تقویم میلادی
 * نمایش داده می‌شود.
 *
 * outputFormat:
 *  - 'gregorian-iso'       → رشته‌ی ISO کامل میلادی (پیش‌فرض — برای فیلدهای Date در دیتابیس)
 *  - 'gregorian-date-only' → 'YYYY-MM-DD' میلادی (برای input[type=date] قدیمی/فرم‌هایی که فقط تاریخ می‌خواهند)
 *  - 'jalali'              → 'YYYY/MM/DD' شمسی خام (برای مواردی مثل احراز هویت ثبت‌احوال که خودِ سرویس تاریخ شمسی می‌خواهد)
 *
 * value باید متناظر با همان outputFormat باشد (میلادی Date/ISO یا رشته‌ی شمسی خام).
 */
// برخی locale های فارسی، خروجی format() را با ارقام فارسی می‌دهند. چون این
// مقادیر معمولاً مستقیم به بک‌اند یا حتی سرویس‌های بیرونی (مثل ثبت‌احوال)
// می‌روند، همیشه به ارقام لاتین/ASCII نرمال می‌کنیم تا parse سمت سرور خراب نشود.
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
function toAsciiDigits(str) {
  return String(str).replace(/[۰-۹٠-٩]/g, (ch) => {
    const pIdx = PERSIAN_DIGITS.indexOf(ch);
    if (pIdx !== -1) return String(pIdx);
    const aIdx = ARABIC_DIGITS.indexOf(ch);
    return aIdx !== -1 ? String(aIdx) : ch;
  });
}

export default function PersianDatePicker({
  value,
  onChange,
  withTime = false,
  outputFormat = 'gregorian-iso',
  className,
  placeholder,
  maxDate,
  minDate,
  onClose,
  disabled,
  required,
}) {
  const { i18n } = useTranslation();
  const IS_PERSIAN = i18n.language === 'fa';

  const defaultPlaceholder = IS_PERSIAN ? 'انتخاب تاریخ' : 'Select Date';
  const activePlaceholder = placeholder || defaultPlaceholder;

  const toDateValue = (v) => {
    if (!v) return '';
    if (outputFormat === 'jalali') {
      try {
        return new DateObject({ date: toAsciiDigits(v), format: 'YYYY/MM/DD', calendar: persian, locale: persian_fa });
      } catch {
        return '';
      }
    }
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return new DateObject({
      date: d,
      calendar: IS_PERSIAN ? persian : undefined,
      locale: IS_PERSIAN ? persian_fa : undefined
    });
  };

  const toBoundary = (v) => {
    if (!v) return undefined;
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return undefined;
    return new DateObject({
      date: d,
      calendar: IS_PERSIAN ? persian : undefined,
      locale: IS_PERSIAN ? persian_fa : undefined
    });
  };

  const handleChange = (dateObject) => {
    if (!onChange) return;
    if (!dateObject) return onChange('');

    if (outputFormat === 'jalali') {
      return onChange(toAsciiDigits(dateObject.format('YYYY/MM/DD')));
    }

    const gDate = dateObject.toDate();
    if (outputFormat === 'gregorian-date-only') {
      const pad = (n) => String(n).padStart(2, '0');
      return onChange(`${gDate.getFullYear()}-${pad(gDate.getMonth() + 1)}-${pad(gDate.getDate())}`);
    }
    return onChange(gDate.toISOString());
  };

  return (
    <DatePicker
      value={toDateValue(value)}
      onChange={handleChange}
      calendar={IS_PERSIAN ? persian : undefined}
      locale={IS_PERSIAN ? persian_fa : undefined}
      className={IS_PERSIAN ? "rmdp-rtl" : ""}
      format={withTime 
        ? (IS_PERSIAN ? 'YYYY/MM/DD HH:mm' : 'YYYY-MM-DD HH:mm') 
        : (IS_PERSIAN ? 'YYYY/MM/DD' : 'YYYY-MM-DD')
      }
      plugins={withTime ? [<TimePicker key="time" position="bottom" hideSeconds />] : []}
      calendarPosition={IS_PERSIAN ? "bottom-right" : "bottom-left"}
      inputClass={`pdp-input${className ? ` ${className}` : ''}`}
      placeholder={activePlaceholder}
      maxDate={toBoundary(maxDate)}
      minDate={toBoundary(minDate)}
      disabled={disabled}
      required={required}
      editable={false}
      onClose={onClose}
      containerStyle={{ width: '100%' }}
    />
  );
}
